import type {
  ModelClientComment,
  ModelClientFieldValue,
} from '@app/api/Client';
import type { CLIENTS, MESSAGES } from '@app/constants';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import type {
  BRANCHES_KEY,
  CHANNEL_IDS_KEY,
  CHANNELS_KEY,
  CRM_TASK_STATUSES_KEY,
  FUNNEL_KEY,
  INTEGRATION_IDS_KEY,
  LEADS_FILTER_KEY,
  MESSAGE_TYPES_KEY,
  PERIOD_KEY,
  SOURCES_KEY,
  STATUS_IDS_KEY,
  TAG_IDS_KEY,
  TASKS_KEY,
  USER_IDS_KEY,
} from '@app/shared/leads/constants/filterKeys';
import type {
  FunnelFilterItem,
  LeadsPeriodType,
} from '@app/shared/leads/types';
import type {
  NBranches,
  NClients,
  NFunnels,
  NIntegrations,
  NMessage,
  NSmallGlobalEntities,
  NTags,
  NUsers,
} from '@app/types';

export namespace NLeads {
  export type ModelLeadFilterData = {
    [CHANNELS_KEY]: ModelItem[] | null;
    Integrations: ModelItem[] | null;
    Users: ModelItem[] | null;
    AllUsers: ModelItem[] | null;
    Tags: ModelItem[] | null;
    Statuses: ModelItem[] | null;
    Branches: ModelItem[] | null;
    CrmTaskStatuses: ModelItem[] | null;
  };

  export type ModelItem = {
    Id: number;
    IdS: string | null;
    Name: string | null;
  };

  export type FilterDate = string | null;

  export type ModelLeadsSearchFilterOptions = Omit<
    ModelLeadsSearchFilter,
    'SearchTerm' | 'ItemsPerPage' | 'Period'
  >;

  export type SearchTermArea = typeof CLIENTS | typeof MESSAGES;

  export type Int32ItemsFilter = {
    OuterInclude: boolean;
    InnerInclude: boolean | null;
    ItemIds: Array<NTags.Tag['Id']> | null;
  };
  export enum TagGroupFilterGroupEnum {
    AND = 'AND',
    OR = 'OR',
    NOT = 'NOT',
  }

  export interface TagGroupFilter {
    id: number;
    type: TagGroupFilterGroupEnum;
    selectedIds: Array<string>;
    toPrevGroupRelationType?: TagGroupFilterGroupEnum;
  }

  export type ModelLeadsSearchFilter = {
    SearchTerm: string;
    [LEADS_FILTER_KEY]: string[];
    [USER_IDS_KEY]: string[];
    [INTEGRATION_IDS_KEY]: string[];
    [CHANNEL_IDS_KEY]: NMessage.ChannelId[];
    [SOURCES_KEY]: string[];
    [STATUS_IDS_KEY]: NMessage.StatusId[];
    [MESSAGE_TYPES_KEY]: string[];
    [TAG_IDS_KEY]: Array<TagGroupFilter>;
    [FUNNEL_KEY]: FunnelFilterItem[];
    [TASKS_KEY]: string[];
    [PERIOD_KEY]: NLeads.PeriodFilter;
    [BRANCHES_KEY]: string[];
    [CRM_TASK_STATUSES_KEY]: string[];
    ItemsPerPage: number;
  };

  export type PeriodFilter = {
    startDate?: string;
    endDate?: string;
    datesFilter?: DatesFilter;
  };

  export type DatesFilter = LeadsPeriodType;

  export interface ModelClientUsername {
    ClientId: number;
    SocialType: string;
    Username: string;
    IsEditable?: boolean;
  }

  export type ModelLead = {
    Id: number;
    CreateTime: string;
    ClientId: number;
    ClientPhoto: string | null;
    BranchId: number | null;
    ClientName: string | null;
    ClientPhone: string | null;
    UserId: NUsers.User['id'] | null;
    UserName: string | null;
    LastUpdate: string;
    LastMessage: string | null;
    Direction: 'in' | 'out' | null;
    UnreadMessage: string | null;
    IsNoAnswer: boolean;
    SocialIntegrationId: NIntegrations.ModelSocialIntegration['Id'] | null;
    SocialIntegrationType: string | null;
    SоcialIntegrationName: string | null;
    ClientTags: NTags.Tag[] | null;
    SpamTime: NSmallGlobalEntities.Date;
    FunnelStages: NFunnels.SalesFunnelStageFilterDto[] | null | undefined;
    UserAnswerTill: string | null;
    ClientAccountName: string | null;
    ClientCrmTasks: NCrmTasks.ModelCrmTask[] | null;
    CurrentCrmTask: NCrmTasks.ModelCrmTaskLite | null;
    IsNoCommunication?: boolean;
    FirstChannelId: string | null;
    AnswerTimeInMinutes?: number | null;
    NoAnswerSince?: string | null;
    TimeUtc?: string | null;
  };

  export type ModelLeadClientView = {
    LeadId: NLeads.ModelLead['Id'];
    ClientId: NClients.ClientLite['Id'];
    BranchId: NBranches.ExistedBranch['id'];
    UserId: NUsers.User['id'] | null;
    Lead: NLeads.ModelLead;
    ClientView: NClients.ModelClientView;
    ClientUsernames?: ClientUsernameItem[] | null;
    Branches: NSmallGlobalEntities.ModelItem[] | null;
    Users: NSmallGlobalEntities.ModelItem[] | null;
    ServerTimeUtc: string;
    LeadClientIds: number[];
    SocialIntegrations: NIntegrations.ModelLeadSocialIntegration[] | null;
    ClientCrmTasks: NCrmTasks.ModelCrmTask[] | null;
    /**
     * Дополнительные поля клиента со значениями. Тот же DTO, что у
     * GetClientFieldValues: FieldName — название поля, Value — значение.
     */
    ClientFields?: ModelClientFieldValue[] | null;
    /**
     * Все неудалённые комментарии клиента: карточка показывает их независимо
     * от того, сколько окон истории подгружено.
     */
    ClientComments?: ModelClientComment[] | null;
    IsAiAutoReply: boolean;
    /**
     * Доступна ли для этого лида кнопка создания записи с помощью ИИ
     * (настройки ИИ-функций организации). По этому флагу показывается кнопка
     * «Записать клиента с помощью ИИ» в композере сообщений.
     */
    IsAiCreateRecord: boolean;
  };

  export interface ClientUsernameItem {
    IdS: string | null;
    Name: string | null;
  }

  export type ModelLeadsTags = {
    LeadIds: number[];
    TagIds: string[];
  };

  export type UseLeadCardHeaderComponentEffectsProps = {
    setUserSocialIntegrations: (
      value: NSmallGlobalEntities.ModelItem[]
    ) => void;
  };

  export type WriteFirstModalFormValues = {
    clientName: string | undefined;
    integrationId: number;
    isNewClient: boolean | undefined;
    userName: string;
  };

  export type ActiveLead = {
    key: number;
    index: number;
  };
}
